import "./Onboading.scss"

import {
  Banner,
  FooterHelp,
  LegacyCard,
  Link,
  Spinner,
  Text,
} from "@shopify/polaris"
import classNames from "classnames"
import { observer } from "mobx-react"
import { useEffect, useRef } from "react"
import { Trans } from "react-i18next"
import { useHistory } from "react-router"

import { showConfirmModal } from "@/components/ConfirmModal/showModal"
import stores from "@/stores"
import { isShopifyEnv } from "@/utils/env-helper"

import OnboardingTopBar from "./components/OnboardingTopBar"
import StepFour from "./steps/StepFour"
import StepOne from "./steps/StepOne"
import StepThree from "./steps/StepThree"
import StepTwo from "./steps/StepTwo"
import {
  reportContinueStep1,
  reportContinueStep2,
  reportContinueStep3,
  reportFinish,
  reportSkip,
  reportView,
} from "./tracking"

const Onboading = observer(() => {
  const history = useHistory()
  const onboarding = stores.OnboardingStore
  const languageAdmin = stores.UserInfoStore.data.language_admin || "en"

  const viewReportedRef = useRef(false)

  useEffect(() => {
    if (!onboarding.isInitialized && !onboarding.isLoading) {
      onboarding.initialize()
    }
  }, [onboarding])

  useEffect(() => {
    if (onboarding.isInitialized && !viewReportedRef.current) {
      viewReportedRef.current = true
      reportView()
    }
  }, [onboarding.isInitialized])

  const goToStep = (step: number) => {
    onboarding.setCurrentStep(step)
  }

  const goBack = (step: number) => {
    onboarding.goBack(step)
  }

  const completeOnboarding = async (isSkip = false) => {
    const ok = isSkip ? await onboarding.skip() : await onboarding.finish()
    if (ok) {
      await Promise.all([
        stores.UserInfoStore.updateUserInfo(),
        stores.CommonStore.updateAuthority(),
        stores.PlanStore.updatePlanData(),
      ])
      if (isSkip) {
        window.track("onboarding_skip")
      }
    }
  }

  const finishOnboarding = async (isSkip = false) => {
    if (isSkip) {
      showConfirmModal({
        showPolarisModalWhenEmbed: true,
        title: t("SkipSetup_0"),
        message: (
          <div className="flex flex-col gap-4">
            <Text as="p" variant="bodyMd">
              {t("14DayFreeTrialStartImmediatelySkipSetup")}
            </Text>
            <Banner tone="info">
              <p>{t("WeRecommendCompletingSetupFitsStoreFirst")}</p>
            </Banner>
          </div>
        ),
        extraPrimaryAction: {
          content: t("ContinueSetup"),
        },
        extraCancelAction: {
          content: t("SkipStartTrial"),
        },
        onConfirm: () => {},
        onCancel: async () => {
          reportSkip()
          await completeOnboarding(true)
          pnav("/Dashboard")
        },
      })
      return
    }

    await completeOnboarding(isSkip)
    pnav("/Dashboard")
  }

  const renderStep = () => {
    const currentStep = onboarding.currentStep
    if (currentStep === 1) {
      return (
        <StepOne
          onContinue={() => {
            reportContinueStep1()
            goToStep(2)
          }}
        />
      )
    }

    if (currentStep === 2) {
      return (
        <StepTwo
          onBack={() => goBack(1)}
          onContinue={() => {
            reportContinueStep2()
            goToStep(3)
          }}
        />
      )
    }

    if (currentStep === 3) {
      return (
        <StepThree
          onBack={() => goBack(2)}
          onContinue={() => {
            reportContinueStep3()
            goToStep(4)
          }}
        />
      )
    }

    return (
      <StepFour
        isSaving={onboarding.isSaving}
        onBack={() => goBack(3)}
        onFinish={async () => {
          reportFinish()
          await finishOnboarding()
        }}
      />
    )
  }

  const currentStep = onboarding.currentStep

  return (
    <div
      id="OnboardingPage"
      className={classNames(
        "flex flex-col items-center px-2 pt-2 pb-0 sm:px-6 sm:pt-6 md:items-center w-full h-full",

        !isShopifyEnv && "min-h-[calc(100vh-56px)]",
      )}
    >
      <LegacyCard key={languageAdmin}>
        <div className="w-full h-full flex flex-1 items-center justify-center">
          {!onboarding.isInitialized ? (
            <Spinner accessibilityLabel={t("LoadingOnboarding")} size="large" />
          ) : (
            <div className=" mx-auto flex w-full max-w-[700px] flex-col gap-5 px-5 py-10 md:px-0 md:py-14">
              {currentStep < 4 && (
                <OnboardingTopBar
                  currentStep={currentStep}
                  isSaving={onboarding.isSaving}
                  onSkip={() => finishOnboarding(true)}
                />
              )}
              {renderStep()}
            </div>
          )}
        </div>
      </LegacyCard>
      <FooterHelp>
        <Trans i18nKey="NeedHelpContactUsAnytime">
          <Link
            external
            removeUnderline
            onClick={() => window.Intercom("showNewMessage", "")}
          />
        </Trans>
      </FooterHelp>
    </div>
  )
})

export default Onboading
